"use client";

import { useEffect, useMemo, type ReactNode } from "react";
import { create } from "zustand";
import { z } from "zod";

import {
  type CartItem,
  type CartTotals,
  type SessionCartItem,
  sessionCartItemSchema,
} from "@/domain/cart/cart-item";
import {
  defaultShippingDestination,
  shippingDestinationSchema,
  type ShippingDestination,
} from "@/domain/cart/shipping-destination";
import { calculateCartPricing } from "@/services/pricing/pricing-service";
import { calculateShippingAmount } from "@/services/pricing/shipping-service";

const cartStorageKey = "ib:ecommerce:cart:v1";
const sessionStorageKey = "ib:ecommerce:session:v1";
const cartVersion = 1;
const cartTtlMs = 72 * 60 * 60 * 1000;

const persistedCartSchema = z.object({
  version: z.literal(cartVersion),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
  submissionId: z.string().uuid().optional(),
  // A destination value that stopped matching the current enum (a rename or a
  // rollback) must never invalidate the whole payload. The persisted field stays
  // structurally loose and the destination is resolved separately below.
  shippingDestination: z.string().optional().catch(undefined),
  items: z.array(sessionCartItemSchema).max(100),
});

interface CartState {
  readonly items: readonly SessionCartItem[];
  readonly sessionId: string | null;
  readonly submissionId: string | null;
  readonly shippingDestination: ShippingDestination;
  readonly storageError: string | null;
  readonly isHydrated: boolean;
}

interface CartActions {
  readonly hydrate: () => void;
  readonly addItem: (item: SessionCartItem, quantity?: number) => void;
  readonly updateQuantity: (productId: string, quantity: number) => void;
  readonly removeItem: (productId: string) => void;
  readonly setShippingDestination: (destination: ShippingDestination) => void;
  readonly clearCart: () => void;
  readonly toOrderItems: () => CartItem[];
}

type CartStore = CartState & CartActions;

interface PersistedCart {
  readonly version: typeof cartVersion;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly submissionId: string;
  readonly shippingDestination?: ShippingDestination;
  readonly items: readonly SessionCartItem[];
}

export interface CartProviderProps {
  readonly children: ReactNode;
}

const useCartStore = create<CartStore>((set, get) => ({
  items: [],
  sessionId: null,
  submissionId: null,
  shippingDestination: defaultShippingDestination,
  storageError: null,
  isHydrated: false,
  hydrate: () => {
    const sessionId = readOrCreateSessionId();
    const persistedCart = readPersistedCart();

    if (persistedCart.ok) {
      const submissionId = persistedCart.submissionId ?? crypto.randomUUID();
      const shippingDestination =
        persistedCart.shippingDestination ?? defaultShippingDestination;

      if (!persistedCart.submissionId || !persistedCart.shippingDestination) {
        persistCart(persistedCart.items, submissionId, shippingDestination);
      }

      set({
        items: persistedCart.items,
        sessionId,
        submissionId,
        shippingDestination,
        storageError: null,
        isHydrated: true,
      });
      return;
    }

    set({
      items: [],
      sessionId,
      submissionId: crypto.randomUUID(),
      shippingDestination: defaultShippingDestination,
      storageError: persistedCart.message,
      isHydrated: true,
    });
  },
  addItem: (item, quantity = 1) =>
    set((state) => {
      const submissionId = crypto.randomUUID();
      const existingItem = state.items.find(
        (cartItem) => cartItem.productId === item.productId,
      );
      const nextQuantity = clampQuantity((existingItem?.quantity ?? 0) + quantity);
      const items = existingItem
        ? state.items.map((cartItem) =>
            cartItem.productId === item.productId
              ? { ...item, quantity: nextQuantity }
              : cartItem,
          )
        : [...state.items, { ...item, quantity: clampQuantity(quantity) }];

      persistCart(items, submissionId, state.shippingDestination);

      return { items, submissionId, storageError: null };
    }),
  updateQuantity: (productId, quantity) =>
    set((state) => {
      const submissionId = crypto.randomUUID();
      const items = state.items.map((item) =>
        item.productId === productId
          ? { ...item, quantity: clampQuantity(quantity) }
          : item,
      );

      persistCart(items, submissionId, state.shippingDestination);

      return { items, submissionId, storageError: null };
    }),
  removeItem: (productId) =>
    set((state) => {
      const submissionId = crypto.randomUUID();
      const items = state.items.filter((item) => item.productId !== productId);

      persistCart(items, submissionId, state.shippingDestination);

      return { items, submissionId, storageError: null };
    }),
  setShippingDestination: (destination) =>
    set((state) => {
      const submissionId = crypto.randomUUID();

      persistCart(state.items, submissionId, destination);

      return { submissionId, shippingDestination: destination, storageError: null };
    }),
  clearCart: () => {
    removePersistedCart();
    set({ items: [], submissionId: crypto.randomUUID(), storageError: null });
  },
  toOrderItems: () =>
    get().items.map((item) => ({
      productId: item.productId,
      sku: item.sku,
      name: item.name,
      quantity: item.quantity,
      salePrice: item.salePrice,
      wholesalePrice: item.wholesalePrice,
      wholesaleMinQuantity: item.wholesaleMinQuantity,
      currencyCode: item.currencyCode,
    })),
}));

export function CartProvider({ children }: Readonly<CartProviderProps>) {
  const hydrate = useCartStore((state) => state.hydrate);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  return children;
}

export function useCart() {
  const items = useCartStore((state) => state.items);
  const sessionId = useCartStore((state) => state.sessionId);
  const submissionId = useCartStore((state) => state.submissionId);
  const shippingDestination = useCartStore((state) => state.shippingDestination);
  const storageError = useCartStore((state) => state.storageError);
  const isHydrated = useCartStore((state) => state.isHydrated);
  const addItem = useCartStore((state) => state.addItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const setShippingDestination = useCartStore((state) => state.setShippingDestination);
  const clearCart = useCartStore((state) => state.clearCart);
  const toOrderItems = useCartStore((state) => state.toOrderItems);
  const pricing = useMemo(() => calculateCartPricing(items), [items]);
  const totals = useMemo(
    () => calculateCartTotals(items, pricing, shippingDestination),
    [items, pricing, shippingDestination],
  );

  return {
    items,
    isHydrated,
    sessionId,
    submissionId,
    shippingDestination,
    storageError,
    totals,
    wholesaleProgress: pricing.wholesaleProgress,
    addItem,
    updateQuantity,
    removeItem,
    setShippingDestination,
    clearCart,
    toOrderItems,
  };
}

function calculateCartTotals(
  items: readonly SessionCartItem[],
  pricing: ReturnType<typeof calculateCartPricing>,
  shippingDestination: ShippingDestination,
): CartTotals {
  const currencyCode = items[0]?.currencyCode ?? null;
  const shippingAmount = calculateShippingAmount(shippingDestination);

  return {
    subtotalRetail: pricing.subtotalRetail,
    subtotalEstimated: pricing.subtotalEstimated,
    wholesaleDiscount: pricing.wholesaleDiscount,
    shippingAmount,
    total: pricing.subtotalEstimated + shippingAmount,
    currencyCode,
    itemCount: pricing.itemCount,
    isWholesaleOrder: pricing.qualification.isWholesaleOrder,
  };
}

function clampQuantity(quantity: number): number {
  return Math.min(999, Math.max(1, Math.trunc(quantity)));
}

function resolvePersistedShippingDestination(
  value: string | undefined,
): ShippingDestination | undefined {
  if (value === undefined) {
    return undefined;
  }

  const parsed = shippingDestinationSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
}

function readPersistedCart():
  | {
      readonly ok: true;
      readonly items: readonly SessionCartItem[];
      readonly submissionId?: string;
      readonly shippingDestination?: ShippingDestination;
    }
  | { readonly ok: false; readonly message: string } {
  const rawValue = window.localStorage.getItem(cartStorageKey);

  if (!rawValue) {
    return { ok: true, items: [] };
  }

  const parsedJson = parseJson(rawValue);

  if (!parsedJson.ok) {
    removePersistedCart();

    return {
      ok: false,
      message: "El carrito guardado estaba danado y fue reiniciado.",
    };
  }

  const parsed = persistedCartSchema.safeParse(parsedJson.value);

  if (!parsed.success) {
    removePersistedCart();

    return {
      ok: false,
      message: "El carrito guardado estaba danado y fue reiniciado.",
    };
  }

  if (Date.now() - parsed.data.updatedAt > cartTtlMs) {
    removePersistedCart();

    return {
      ok: false,
      message: "El carrito guardado expiro despues de 72 horas y fue reiniciado.",
    };
  }

  return {
    ok: true,
    items: parsed.data.items,
    submissionId: parsed.data.submissionId,
    shippingDestination: resolvePersistedShippingDestination(
      parsed.data.shippingDestination,
    ),
  };
}

function persistCart(
  items: readonly SessionCartItem[],
  submissionId: string,
  shippingDestination: ShippingDestination,
): void {
  const now = Date.now();
  const currentCart = readCurrentPersistedCart();
  const persistedCart: PersistedCart = {
    version: cartVersion,
    createdAt: currentCart?.createdAt ?? now,
    updatedAt: now,
    submissionId,
    shippingDestination,
    items,
  };

  window.localStorage.setItem(cartStorageKey, JSON.stringify(persistedCart));
}

function readCurrentPersistedCart(): PersistedCart | null {
  const rawValue = window.localStorage.getItem(cartStorageKey);

  if (!rawValue) {
    return null;
  }

  const parsedJson = parseJson(rawValue);

  if (!parsedJson.ok) {
    return null;
  }

  const parsed = persistedCartSchema.safeParse(parsedJson.value);

  if (!parsed.success) {
    return null;
  }

  return {
    createdAt: parsed.data.createdAt,
    items: parsed.data.items,
    submissionId: parsed.data.submissionId ?? crypto.randomUUID(),
    shippingDestination: resolvePersistedShippingDestination(
      parsed.data.shippingDestination,
    ),
    updatedAt: parsed.data.updatedAt,
    version: parsed.data.version,
  };
}

function removePersistedCart(): void {
  window.localStorage.removeItem(cartStorageKey);
}

function parseJson(rawValue: string):
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false } {
  try {
    return { ok: true, value: JSON.parse(rawValue) as unknown };
  } catch {
    return { ok: false };
  }
}

function readOrCreateSessionId(): string {
  const savedSessionId = window.localStorage.getItem(sessionStorageKey);

  if (savedSessionId && z.string().uuid().safeParse(savedSessionId).success) {
    return savedSessionId;
  }

  const sessionId = crypto.randomUUID();
  window.localStorage.setItem(sessionStorageKey, sessionId);

  return sessionId;
}
